import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { PageLoader } from "@/components/common/Loading";
import { EmptyState } from "@/components/common/EmptyState";
import { DataTable } from "@/components/common/DataTable";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { JobDialog, viewResume } from "./Jobs";
import { ArrowLeft, ExternalLink, Copy, FileText, Pencil, Download } from "lucide-react";
import { toast } from "sonner";

const JOB_STATUS = { draft: "Draft", open: "Open", paused: "Paused", closed: "Closed", archived: "Archived" };
const APP_STATUS = { new: "New", reviewed: "Reviewed", advanced: "Advanced", rejected: "Rejected", withdrawn: "Withdrawn" };

export default function JobDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [edit, setEdit] = useState(false);
  const load = useCallback(() => api.get(`/jobs/${id}`).then((r) => setData(r.data)).catch((e) => { toast.error(apiError(e)); navigate("/jobs"); }), [id, navigate]);
  useEffect(() => { load(); }, [load]);
  if (!data) return <PageLoader />;
  const j = data.job;
  const publicUrl = j.apply_code ? `${window.location.origin}/j/${j.apply_code}` : `${window.location.origin}/careers/${j.id}`;
  const setStatus = async (v) => { try { await api.patch(`/jobs/${id}`, { status: v }); load(); } catch (e) { toast.error(apiError(e)); } };
  const setAppStatus = async (a, v) => { try { await api.patch(`/applications/${a.id}`, { status: v }); load(); } catch (e) { toast.error(apiError(e)); } };
  const cols = [
    { key: "applicant", header: "Applicant", render: (r) => <button onClick={() => navigate(`/people/detail/${r.person_id}`)} className="font-medium text-slate-800 hover:text-brand">{r.applicant_name}</button> },
    { key: "source", header: "Source", render: (r) => r.source || "—" },
    { key: "submitted", header: "Submitted", render: (r) => r.created_at ? new Date(r.created_at).toLocaleDateString() : "—" },
    { key: "resume", header: "Résumé", render: (r) => r.attachment_id ? (
      <button onClick={() => viewResume(r.attachment_id)} data-testid={`jobapp-resume-${r.id}`}
        className="text-brand hover:underline text-sm flex items-center gap-1"><Download className="w-3.5 h-3.5" />View</button>
    ) : <span className="text-slate-300">—</span> },
    { key: "status", header: "Status", render: (r) => (
      <Select value={r.status} onValueChange={(v) => setAppStatus(r, v)}>
        <SelectTrigger className="h-8 w-32 text-xs" data-testid={`jobapp-status-${r.id}`}><SelectValue /></SelectTrigger>
        <SelectContent className="bg-white">{Object.entries(APP_STATUS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
      </Select>) },
  ];
  return (
    <div className="max-w-4xl">
      <button onClick={() => navigate("/jobs")} className="flex items-center gap-1 text-sm text-slate-400 hover:text-brand mb-3" data-testid="back-to-jobs"><ArrowLeft className="w-4 h-4" /> Jobs</button>
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div>
          <h1 className="font-display text-2xl font-bold text-slate-900" data-testid="jobdetail-title">{j.title}</h1>
          <p className="text-sm text-slate-400">{j.human_id} · {j.organization_name || "—"}{j.location ? ` · ${j.location}` : ""}</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setEdit(true)} data-testid="edit-job"><Pencil className="w-4 h-4 mr-1.5" />Edit</Button>
          <Select value={j.status} onValueChange={setStatus}>
            <SelectTrigger className="w-32 h-9" data-testid="jobdetail-status"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">{Object.entries(JOB_STATUS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-3 mb-5">
        <div className="lg:col-span-2 rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
          <p className="text-sm text-slate-600 whitespace-pre-wrap">{j.description || "No description."}</p>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm space-y-2 text-sm">
          <p><span className="text-slate-400 text-xs uppercase">Candidate Type</span><br />{j.candidate_type || "—"}</p>
          <p><span className="text-slate-400 text-xs uppercase">Recruiter</span><br />{j.assigned_recruiter_name || "—"}</p>
          <p><span className="text-slate-400 text-xs uppercase">Applications</span><br />{data.applications.length}</p>
          {j.status === "open" && j.is_public && (
            <div className="pt-2 border-t border-slate-100">
              <a href={publicUrl} target="_blank" rel="noreferrer" className="text-brand text-xs flex items-center gap-1 hover:underline" data-testid="public-job-link"><ExternalLink className="w-3 h-3" />View public page</a>
              <button onClick={() => { navigator.clipboard.writeText(publicUrl); toast.success("Link copied"); }} className="text-slate-400 text-xs flex items-center gap-1 hover:text-brand mt-1"><Copy className="w-3 h-3" />Copy apply link</button>
            </div>
          )}
        </div>
      </div>

      <h3 className="font-display font-semibold text-slate-800 mb-2 flex items-center gap-2"><FileText className="w-4 h-4 text-brand" />Applications</h3>
      <DataTable columns={cols} rows={data.applications} testId="jobdetail-applications"
        empty={<EmptyState icon={FileText} title="No applications yet" description="Share the public apply link to receive applications." />} />
      {edit && <JobDialog job={j} onClose={() => setEdit(false)} onSaved={() => { setEdit(false); load(); }} />}
    </div>
  );
}
